export type AnalyticsEventName =
  | "book_meeting_click"
  | "contact_email_click"
  | "contact_form_submit"
  | "project_brief_start"
  | "project_brief_submit"
  | "service_card_click"
  | "pricing_card_click"
  | "case_cta_click"
  | "language_switch"
  | "newsletter_signup";

export type AnalyticsParams = Record<
  string,
  string | number | boolean | null | undefined
>;

type ConsentValue = "granted" | "denied";
type ProviderStatus = "idle" | "loading" | "ready" | "failed";
type GtagFunction = (...args: unknown[]) => void;

const UTM_PARAM_NAMES = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
] as const;

type UtmParams = Partial<Record<(typeof UTM_PARAM_NAMES)[number], string>>;

type LandingSnapshot = {
  landing_page: string;
  landing_date: string;
  utm?: UtmParams;
  referrer_host?: string;
};

type AttributionState = {
  version: 1;
  first: LandingSnapshot;
  latest: LandingSnapshot;
  updated_at: string;
};

type QueuedEvent = {
  name: AnalyticsEventName;
  params: Record<string, string | number | boolean>;
};

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: GtagFunction;
    NalosoftAnalytics?: {
      grantConsent: () => void;
      revokeConsent: () => void;
      trackEvent: (
        name: AnalyticsEventName,
        params?: AnalyticsParams
      ) => void;
      getAttribution: () => AttributionState | null;
    };
  }
}

const GA_SCRIPT_ID = "nalosoft-ga4-script";
const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;
const REQUIRE_CONSENT =
  process.env.REACT_APP_ANALYTICS_REQUIRE_CONSENT !== "false";
const CONSENT_STORAGE_KEY = "nalosoft.analytics.consent";
const ATTRIBUTION_STORAGE_KEY = "nalosoft.attribution.v1";
const ATTRIBUTION_SESSION_KEY = "nalosoft.attribution.session.v1";
const ATTRIBUTION_TTL_DAYS = 90;
const MAX_PENDING_EVENTS = 20;
const BLOCKED_PARAM_KEYS = new Set([
  "email",
  "phone",
  "name",
  "message",
  "contact_email",
  "contact_phone",
]);

let providerStatus: ProviderStatus = "idle";
let pendingEvents: QueuedEvent[] = [];

export function initializeAnalytics() {
  captureAttribution();
  installBrowserBridge();

  if (hasAnalyticsConsent()) {
    loadGaProvider();
  }
}

export function captureAttribution() {
  if (!isBrowser()) return;

  const snapshot = buildLandingSnapshot();
  const hasUtm = Boolean(snapshot.utm && Object.keys(snapshot.utm).length);
  const sessionStorage = getStorage("session");
  const sessionCaptured = storageGet(sessionStorage, ATTRIBUTION_SESSION_KEY);

  if (sessionCaptured === "1" && !hasUtm) return;

  const current = readAttributionState();
  const next: AttributionState = current
    ? {
        ...current,
        latest: snapshot,
        updated_at: new Date().toISOString(),
      }
    : {
        version: 1,
        first: snapshot,
        latest: snapshot,
        updated_at: new Date().toISOString(),
      };

  saveAttributionState(next);
  storageSet(sessionStorage, ATTRIBUTION_SESSION_KEY, "1");
}

export function trackAnalyticsEvent(
  name: AnalyticsEventName,
  params: AnalyticsParams = {}
) {
  try {
    captureAttribution();

    if (!isBrowser() || !GA_MEASUREMENT_ID || !hasAnalyticsConsent()) {
      return;
    }

    if (providerStatus === "failed") return;

    const eventParams = normalizeEventParams({
      ...commonEventParams(),
      ...attributionEventParams(),
      ...params,
    });

    if (providerStatus === "idle") {
      loadGaProvider();
    }

    if (providerStatus !== "ready" || !window.gtag) {
      queueEvent({ name, params: eventParams });
      return;
    }

    window.gtag("event", name, eventParams);
  } catch {
    // Analytics must never interrupt the visitor experience.
  }
}

export function grantAnalyticsConsent() {
  storageSet(getStorage("local"), CONSENT_STORAGE_KEY, "granted");
  persistSessionAttribution();

  if (isBrowser() && window.gtag) {
    window.gtag("consent", "update", {
      analytics_storage: "granted",
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
    });
  }

  loadGaProvider();
}

export function revokeAnalyticsConsent() {
  storageSet(getStorage("local"), CONSENT_STORAGE_KEY, "denied");
  storageRemove(getStorage("local"), ATTRIBUTION_STORAGE_KEY);
  pendingEvents = [];

  if (isBrowser() && window.gtag) {
    window.gtag("consent", "update", {
      analytics_storage: "denied",
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
    });
  }
}

export function getAttribution() {
  return readAttributionState();
}

export function getLeadAttributionFields(prefix = "attribution") {
  captureAttribution();

  const attribution = readAttributionState();
  if (!attribution) return {};

  const fields: Record<string, string> = {
    [`${prefix}_first_landing_page`]: attribution.first.landing_page,
    [`${prefix}_first_landing_date`]: attribution.first.landing_date,
    [`${prefix}_latest_landing_page`]: attribution.latest.landing_page,
    [`${prefix}_latest_landing_date`]: attribution.latest.landing_date,
  };

  if (attribution.first.referrer_host) {
    fields[`${prefix}_first_referrer_host`] =
      attribution.first.referrer_host;
  }

  if (attribution.latest.referrer_host) {
    fields[`${prefix}_latest_referrer_host`] =
      attribution.latest.referrer_host;
  }

  appendUtmFields(fields, `${prefix}_first`, attribution.first.utm);
  appendUtmFields(fields, `${prefix}_latest`, attribution.latest.utm);

  return fields;
}

function loadGaProvider() {
  if (!isBrowser() || !GA_MEASUREMENT_ID || !hasAnalyticsConsent()) return;
  if (providerStatus === "ready" || providerStatus === "loading") return;

  const existingScript = document.getElementById(GA_SCRIPT_ID);
  if (existingScript) {
    providerStatus = "ready";
    flushPendingEvents();
    return;
  }

  providerStatus = "loading";
  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function gtag() {
      window.dataLayer?.push(Array.prototype.slice.call(arguments));
    };

  window.gtag("consent", "default", {
    analytics_storage: "granted",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    security_storage: "granted",
  });
  window.gtag("js", new Date());
  window.gtag("config", GA_MEASUREMENT_ID, {
    send_page_view: false,
    anonymize_ip: true,
  });

  const script = document.createElement("script");
  script.id = GA_SCRIPT_ID;
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(
    GA_MEASUREMENT_ID
  )}`;
  script.onload = () => {
    providerStatus = "ready";
    flushPendingEvents();
  };
  script.onerror = () => {
    providerStatus = "failed";
    pendingEvents = [];
  };

  document.head.appendChild(script);
}

function flushPendingEvents() {
  if (!isBrowser() || !window.gtag || !hasAnalyticsConsent()) return;

  const events = pendingEvents;
  pendingEvents = [];
  events.forEach((event) => {
    window.gtag?.("event", event.name, event.params);
  });
}

function queueEvent(event: QueuedEvent) {
  if (pendingEvents.length >= MAX_PENDING_EVENTS) {
    pendingEvents = pendingEvents.slice(1);
  }
  pendingEvents.push(event);
}

function installBrowserBridge() {
  if (!isBrowser()) return;

  window.NalosoftAnalytics = {
    grantConsent: grantAnalyticsConsent,
    revokeConsent: revokeAnalyticsConsent,
    trackEvent: trackAnalyticsEvent,
    getAttribution,
  };
}

function hasAnalyticsConsent() {
  if (!isBrowser() || browserPrivacyOptOut()) return false;
  if (!REQUIRE_CONSENT) return true;
  return readConsent() === "granted";
}

function readConsent(): ConsentValue | null {
  const value = storageGet(getStorage("local"), CONSENT_STORAGE_KEY);
  return value === "granted" || value === "denied" ? value : null;
}

function browserPrivacyOptOut() {
  if (!isBrowser()) return true;

  const navigatorWithSignals = window.navigator as Navigator & {
    globalPrivacyControl?: boolean;
    msDoNotTrack?: string;
  };

  return (
    navigatorWithSignals.globalPrivacyControl === true ||
    navigatorWithSignals.doNotTrack === "1" ||
    navigatorWithSignals.doNotTrack === "yes" ||
    navigatorWithSignals.msDoNotTrack === "1"
  );
}

function buildLandingSnapshot(): LandingSnapshot {
  const location = window.location;
  const landingPage = sanitizeLandingPage(location.pathname);
  const utm = readUtmParams(location.search);
  const referrerHost = readReferrerHost();
  const snapshot: LandingSnapshot = {
    landing_page: landingPage,
    landing_date: dateOnly(new Date()),
  };

  if (Object.keys(utm).length) {
    snapshot.utm = utm;
  }

  if (referrerHost) {
    snapshot.referrer_host = referrerHost;
  }

  return snapshot;
}

function readUtmParams(search: string): UtmParams {
  const params = new URLSearchParams(search);
  const utm: UtmParams = {};

  UTM_PARAM_NAMES.forEach((key) => {
    const value = sanitizeParamValue(params.get(key));
    if (value) {
      utm[key] = value;
    }
  });

  return utm;
}

function readReferrerHost() {
  if (!document.referrer) return undefined;

  try {
    const referrer = new URL(document.referrer);
    if (referrer.hostname === window.location.hostname) return undefined;
    return sanitizeParamValue(referrer.hostname);
  } catch {
    return undefined;
  }
}

function readAttributionState(): AttributionState | null {
  const sessionState = readAttributionFromStorage(getStorage("session"));
  if (sessionState) return sessionState;

  const localState = readAttributionFromStorage(getStorage("local"));
  if (localState) return localState;

  return null;
}

function readAttributionFromStorage(storage: Storage | null) {
  const raw = storageGet(storage, ATTRIBUTION_STORAGE_KEY);
  if (!raw) return null;

  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isAttributionState(parsed) || attributionExpired(parsed)) {
      storageRemove(storage, ATTRIBUTION_STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    storageRemove(storage, ATTRIBUTION_STORAGE_KEY);
    return null;
  }
}

function saveAttributionState(state: AttributionState) {
  const serialized = JSON.stringify(state);
  storageSet(getStorage("session"), ATTRIBUTION_STORAGE_KEY, serialized);

  if (hasAnalyticsConsent()) {
    storageSet(getStorage("local"), ATTRIBUTION_STORAGE_KEY, serialized);
  }
}

function persistSessionAttribution() {
  const state = readAttributionFromStorage(getStorage("session"));
  if (state) {
    storageSet(
      getStorage("local"),
      ATTRIBUTION_STORAGE_KEY,
      JSON.stringify(state)
    );
  }
}

function attributionEventParams(): AnalyticsParams {
  const attribution = readAttributionState();
  if (!attribution) return {};

  return {
    first_landing_page: attribution.first.landing_page,
    latest_landing_page: attribution.latest.landing_page,
    landing_referrer_host: attribution.latest.referrer_host,
    ...attribution.latest.utm,
  };
}

function appendUtmFields(
  fields: Record<string, string>,
  prefix: string,
  utm: UtmParams | undefined
) {
  if (!utm) return;

  UTM_PARAM_NAMES.forEach((key) => {
    const value = utm[key];
    if (value) {
      fields[`${prefix}_${key}`] = value;
    }
  });
}

function commonEventParams(): AnalyticsParams {
  return {
    page_path: sanitizeLandingPage(window.location.pathname),
    page_title: document.title,
    language: document.documentElement.lang || languageFromPathname(),
  };
}

function normalizeEventParams(params: AnalyticsParams) {
  const normalized: Record<string, string | number | boolean> = {};

  Object.entries(params).forEach(([key, value]) => {
    const safeKey = sanitizeParamKey(key);
    if (!safeKey || BLOCKED_PARAM_KEYS.has(safeKey)) return;

    const safeValue = sanitizeEventValue(value);
    if (safeValue !== undefined) {
      normalized[safeKey] = safeValue;
    }
  });

  return normalized;
}

function sanitizeEventValue(value: AnalyticsParams[string]) {
  if (value === null || value === undefined) return undefined;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return Number.isFinite(value) ? value : undefined;

  return sanitizeParamValue(value);
}

function sanitizeParamKey(key: string) {
  const clean = key.replace(/[^a-zA-Z0-9_]/g, "_").slice(0, 40);
  if (!clean) return undefined;
  if (/^[a-zA-Z]/.test(clean)) return clean;
  return `p_${clean}`.slice(0, 40);
}

function sanitizeParamValue(value: string | null | undefined) {
  if (!value) return undefined;

  const clean = stripControlCharacters(value.trim(), 120);

  if (!clean || /\S+@\S+\.\S+/.test(clean) || /https?:\/\//i.test(clean)) {
    return undefined;
  }

  return clean;
}

function stripControlCharacters(value: string, maxLength: number) {
  let clean = "";

  for (let index = 0; index < value.length; index += 1) {
    const charCode = value.charCodeAt(index);
    if (charCode <= 31 || charCode === 127) continue;

    clean += value.charAt(index);
    if (clean.length >= maxLength) break;
  }

  return clean;
}

function sanitizeLandingPage(pathname: string) {
  const path = pathname || "/";
  return path.length > 120 ? path.slice(0, 120) : path;
}

function languageFromPathname() {
  const match = window.location.pathname.match(/^\/(en|da|sv)(?=\/|$)/);
  return match?.[1] || "en";
}

function attributionExpired(state: AttributionState) {
  const updatedAt = Date.parse(state.updated_at);
  if (isNaN(updatedAt)) return true;

  const maxAgeMs = ATTRIBUTION_TTL_DAYS * 24 * 60 * 60 * 1000;
  return Date.now() - updatedAt > maxAgeMs;
}

function isAttributionState(value: unknown): value is AttributionState {
  if (!isRecord(value)) return false;
  return (
    value.version === 1 &&
    isLandingSnapshot(value.first) &&
    isLandingSnapshot(value.latest) &&
    typeof value.updated_at === "string"
  );
}

function isLandingSnapshot(value: unknown): value is LandingSnapshot {
  if (!isRecord(value)) return false;
  return (
    typeof value.landing_page === "string" &&
    typeof value.landing_date === "string"
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function dateOnly(date: Date) {
  return date.toISOString().slice(0, 10);
}

function getStorage(kind: "local" | "session") {
  if (!isBrowser()) return null;

  try {
    return kind === "local" ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
}

function storageGet(storage: Storage | null, key: string) {
  try {
    return storage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function storageSet(storage: Storage | null, key: string, value: string) {
  try {
    storage?.setItem(key, value);
  } catch {
    // Storage can be unavailable in private contexts.
  }
}

function storageRemove(storage: Storage | null, key: string) {
  try {
    storage?.removeItem(key);
  } catch {
    // Storage can be unavailable in private contexts.
  }
}

function isBrowser() {
  return typeof window !== "undefined" && typeof document !== "undefined";
}
